/**
 * Public, no-login demo: /demo
 *
 * Type nutrition numbers, watch a compliant FDA Nutrition Facts panel assemble live.
 *
 * It reuses the REAL engine and renderer — `buildPanel` from @easynutrifact/label-engine and
 * `<LabelPreview>` (which renders the same SVG the print export rasterizes). So the panel a
 * visitor sees here is not a mockup: it is the product, with genuine 21 CFR 101.9 rounding.
 * That is the whole pitch, and faking it would be both dishonest and less convincing.
 *
 * Self-contained: no auth, no database, no Shopify. Prefilled with the FDA's own published
 * granola example so the first paint already shows a finished panel — a blank form converts
 * far worse than a working one.
 *
 * Mobile-first: short-form/ad traffic is ~90% phone. Every input and button clears 44px.
 */
import { useMemo, useState } from "react";
import { buildPanel, type PanelModel } from "@easynutrifact/label-engine";
import { LabelPreview } from "../components/LabelPreview";
import {
  marketingLinks,
  MarketingShell,
  socialImageMeta,
} from "../components/marketing/layout";
import {
  SERVING_FIELDS,
  nutrientFieldName,
  sampleFieldValues,
  valuesToCalcInput,
  hasEnoughForPreview,
  type FieldValues,
} from "../lib/label-form";
import { SITE_ORIGIN } from "../brand";

// The demo's .nld__* styles live in app/styles/marketing.css alongside the rest
// of the public design system — routes.ts uses flatRoutes(), which treats every
// file under app/routes/ as a route module (a .css file there fails the build).
export const links = marketingLinks;

export const meta = () => [
  { title: "Build an FDA Nutrition Facts label free — live demo" },
  {
    name: "description",
    content:
      "Enter nutrition data and preview an FDA-style Nutrition Facts panel with 21 CFR 101.9 rounding. No account or install required.",
  },
  { name: "robots", content: "index,follow" },
  { tagName: "link", rel: "canonical", href: `${SITE_ORIGIN}/demo` },
  { property: "og:url", content: `${SITE_ORIGIN}/demo` },
  { property: "og:type", content: "website" },
  { property: "og:title", content: "Build an FDA-style Nutrition Facts label — free live demo" },
  {
    property: "og:description",
    content: "Real 21 CFR rounding, real print-ready panel. No account, no install.",
  },
  ...socialImageMeta(),
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "EasyNutriFact live demo",
      url: `${SITE_ORIGIN}/demo`,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "Legitimate LLC" },
    },
  },
];

/** The fields worth exposing on a phone. The full editor has every optional nutrient. */
const FIELDS: Array<{ id: string; label: string; unit: string; name: string }> = [
  { id: "energy", label: "Calories", unit: "", name: nutrientFieldName("energy") },
  { id: "fat_total", label: "Total fat", unit: "g", name: nutrientFieldName("fat_total") },
  { id: "fat_sat", label: "Saturated fat", unit: "g", name: nutrientFieldName("fat_sat") },
  { id: "sodium", label: "Sodium", unit: "mg", name: nutrientFieldName("sodium") },
  { id: "carb_total", label: "Total carbs", unit: "g", name: nutrientFieldName("carb_total") },
  { id: "fiber", label: "Dietary fiber", unit: "g", name: nutrientFieldName("fiber") },
  { id: "sugars_total", label: "Total sugars", unit: "g", name: nutrientFieldName("sugars_total") },
  { id: "sugars_added", label: "Added sugars", unit: "g", name: nutrientFieldName("sugars_added") },
  { id: "protein", label: "Protein", unit: "g", name: nutrientFieldName("protein") },
];

export default function NutritionLabelDemo() {
  // Prefilled: a finished panel on first paint beats an empty form.
  const [values, setValues] = useState<FieldValues>(() => sampleFieldValues());

  const set = (name: string, v: string) =>
    setValues((prev) => ({ ...prev, [name]: v }));

  const { panel, error } = useMemo((): { panel: PanelModel | null; error: string | null } => {
    if (!hasEnoughForPreview(values)) return { panel: null, error: null };
    try {
      const result = buildPanel(valuesToCalcInput(values));
      return { panel: result.panel, error: null };
    } catch {
      // Never let a transient input state blank the page.
      return { panel: null, error: "Enter a calorie value to build the panel." };
    }
  }, [values]);

  return (
    <MarketingShell>
      <div className="nm-wrap">
        <div className="nld">
          <header className="nld__head">
            <p className="nld__eyebrow">Live demo · nothing is saved</p>
            <h1 className="nld__h1">Your Nutrition Facts panel, building as you type.</h1>
            <p className="nld__lead">
              These are the FDA&rsquo;s own published example numbers. Change any of them and watch the
              panel re-round itself — <strong>21 CFR 101.9 rounding rules applied automatically</strong>,
              which is the part that gets labels rejected when it&rsquo;s done by hand.
            </p>
          </header>

          <div className="nld__grid">
        {/* ---- inputs ---- */}
        <section className="nld__form" aria-label="Nutrition values">
          <fieldset className="nld__set">
            <legend className="nld__legend">Serving</legend>
            <label className="nld__field">
              <span>Serving size</span>
              <input
                type="text"
                inputMode="text"
                value={values[SERVING_FIELDS.householdMeasure] ?? ""}
                onChange={(e) => set(SERVING_FIELDS.householdMeasure, e.target.value)}
              />
            </label>
            <div className="nld__row">
              <label className="nld__field">
                <span>Grams</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={values[SERVING_FIELDS.metricGrams] ?? ""}
                  onChange={(e) => set(SERVING_FIELDS.metricGrams, e.target.value)}
                />
              </label>
              <label className="nld__field">
                <span>Servings/container</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={values[SERVING_FIELDS.servingsPerContainer] ?? ""}
                  onChange={(e) => set(SERVING_FIELDS.servingsPerContainer, e.target.value)}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="nld__set">
            <legend className="nld__legend">Per serving</legend>
            {FIELDS.map((f) => (
              <label className="nld__field nld__field--inline" key={f.id}>
                <span>
                  {f.label}
                  {f.unit ? <em className="nld__unit"> ({f.unit})</em> : null}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={values[f.name] ?? ""}
                  onChange={(e) => set(f.name, e.target.value)}
                />
              </label>
            ))}
          </fieldset>

          <div className="nld__formActions">
            <button
              type="button"
              className="nld__ghost"
              onClick={() => setValues(sampleFieldValues())}
            >
              Reset to FDA example
            </button>
          </div>
        </section>

        {/* ---- live panel ---- */}
        <section className="nld__panelCol" aria-label="Label preview">
          <div className="nld__panelSticky">
            {panel ? (
              <LabelPreview panel={panel} />
            ) : (
              <p className="nld__empty">{error ?? "Enter a calorie value to build the panel."}</p>
            )}
            <p className="nld__fine">
              This is the same renderer the app&rsquo;s export rasterizes — so what you see is what a
              300&nbsp;DPI print-ready PNG would contain.
            </p>
          </div>
        </section>
      </div>

      <section className="nld__cta">
        <h2 className="nld__h2">In your Shopify admin, this becomes a real asset</h2>
        <ul className="nld__points">
          <li>
            <strong>Print-ready 300 DPI PNG</strong> — sized for packaging, not just the web.
          </li>
          <li>
            <strong>On the product page too</strong> — a theme block renders the label on the PDP.
          </li>
          <li>
            <strong>Rounding you don&rsquo;t have to audit</strong> — the increments and
            &ldquo;less than&rdquo; thresholds follow 21&nbsp;CFR&nbsp;101.9 by construction.
          </li>
        </ul>
        <a
          className="nld__install"
          href="https://apps.shopify.com/labelforge-1"
          target="_blank"
          rel="noreferrer"
        >
          Install free on Shopify
        </a>
        <p className="nld__fine">
          Free — no tiers, no export fee. This demo stores nothing and needs no account.
        </p>
      </section>
        </div>
      </div>
    </MarketingShell>
  );
}
